import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { dashboardAPI } from '../services/api';
import { format } from 'date-fns';

function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await dashboardAPI.getMetrics();
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard metrics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Open': 'danger',
      'In Progress': 'primary',
      'Resolved': 'success',
      'Closed': 'secondary',
      'Re-opened': 'warning',
      'Not a Bug': 'info',
      'Cannot Reproduce': 'secondary',
      'Pending QA': 'warning'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'primary',
      'Low': 'secondary'
    };
    return <Badge bg={priorityColors[priority] || 'secondary'}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid style={{ padding: '2rem 3rem' }}>
      <h2 className="mb-3 fw-bold" style={{ fontSize: '28px', color: '#1a1a1a' }}>Dashboard</h2>

      {/* Metrics Cards */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.25rem' }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Total Open</p>
                  <h3 className="fw-bold mb-0" style={{ fontSize: '32px' }}>{metrics?.total_open || 0}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 p-2 rounded-circle" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-bug text-danger" style={{ fontSize: '22px' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.25rem' }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Closed</p>
                  <h3 className="fw-bold mb-0" style={{ fontSize: '32px' }}>{metrics?.total_closed || 0}</h3>
                </div>
                <div className="bg-success bg-opacity-10 p-2 rounded-circle" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-check-circle text-success" style={{ fontSize: '22px' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.25rem' }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Re-opened</p>
                  <h3 className="fw-bold mb-0" style={{ fontSize: '32px' }}>{metrics?.total_reopened || 0}</h3>
                </div>
                <div className="bg-warning bg-opacity-10 p-2 rounded-circle" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-arrow-repeat text-warning" style={{ fontSize: '22px' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.25rem' }}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '13px', fontWeight: '500' }}>Avg. Turnaround (hrs)</p>
                  <h3 className="fw-bold mb-0" style={{ fontSize: '32px' }}>{metrics?.average_turnaround_time || 0}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-2 rounded-circle" style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-clock text-primary" style={{ fontSize: '22px' }}></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status & Priority Charts */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h5 className="fw-bold mb-3" style={{ fontSize: '18px', color: '#1a1a1a' }}>Bugs by Status</h5>
              <div>
                {metrics?.bugs_by_status && Object.entries(metrics.bugs_by_status).map(([status, count]) => (
                  <div key={status} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: '14px', color: '#555' }}>{status}</span>
                    <Badge bg="secondary" style={{ borderRadius: '12px', padding: '4px 12px', fontSize: '13px', fontWeight: '500' }}>{count}</Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <h5 className="fw-bold mb-3" style={{ fontSize: '18px', color: '#1a1a1a' }}>Bugs by Priority</h5>
              <div>
                {metrics?.bugs_by_priority && Object.entries(metrics.bugs_by_priority).map(([priority, count]) => (
                  <div key={priority} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: '14px', color: '#555' }}>{priority}</span>
                    <Badge bg="secondary" style={{ borderRadius: '12px', padding: '4px 12px', fontSize: '13px', fontWeight: '500' }}>{count}</Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Bugs */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
        <Card.Body style={{ padding: '1.5rem' }}>
          <h5 className="fw-bold mb-3" style={{ fontSize: '18px', color: '#1a1a1a' }}>Recent Bugs</h5>
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead style={{ borderBottom: '2px solid #e0e0e0' }}>
                <tr>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Bug ID</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Title</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Project</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Priority</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Status</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Reporter</th>
                  <th style={{ fontSize: '13px', fontWeight: '600', color: '#666', padding: '12px 8px', backgroundColor: 'transparent' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {metrics?.recent_bugs && metrics.recent_bugs.length > 0 ? (
                  metrics.recent_bugs.map((bug) => (
                    <tr key={bug.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ fontSize: '14px', padding: '12px 8px' }}>
                        <Link to={`/bugs/${bug.id}`} className="text-decoration-none fw-semibold">
                          {bug.bug_number}
                        </Link>
                      </td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#333' }}>{bug.title}</td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.project_name}</td>
                      <td style={{ padding: '12px 8px' }}>{getPriorityBadge(bug.priority)}</td>
                      <td style={{ padding: '12px 8px' }}>{getStatusBadge(bug.status)}</td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#666' }}>{bug.reporter_name}</td>
                      <td style={{ fontSize: '14px', padding: '12px 8px', color: '#999' }}>{format(new Date(bug.created_at), 'MMM dd, yyyy')}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted" style={{ padding: '24px' }}>
                      No bugs found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Dashboard;
